import React, { useState } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useEffectiveUser } from '@fs/zion-user'
import { usePreference } from '@fs/zion-preferences'
import { i18n } from '@fs/zion-locale'
import { AamInitialState, aamReducer, ACTIONS } from './AamReducer'
import { checkAsianChar, deconstructName, getDisplayName } from '../../../shared/helpers/name_formatting'
import { useTileData, useAncestorYearMatch } from '../hooks/hooks'
import { useDetailedFamilyTreeData } from '../../../shared/hooks/useDetailedFamilyTreeData'
import setCookie from '../../../shared/helpers/cookie'
import { extractFourDigitYear } from '../../../shared/helpers/factDate_Helper'
import { useDSCState, useDSCDispatch, ACTIONS as DSC_ACTIONS } from '../../../state/DSCStore'
import { shouldClearNonstandardUser } from '../../../state/DSCReducer'
import countryDetails from '../../../shared/data/countryDetails.json'

const AamStateContext = React.createContext()
const AamDispatchContext = React.createContext()

function AamProvider({ children }) {
  const [aamGlobalState, aamGlobalDispatch] = React.useReducer(aamReducer, AamInitialState)
  return (
    <AamStateContext.Provider value={aamGlobalState}>
      <AamDispatchContext.Provider value={aamGlobalDispatch}>{children}</AamDispatchContext.Provider>
    </AamStateContext.Provider>
  )
}

function useAamState() {
  const context = React.useContext(AamStateContext)
  if (context === undefined) {
    throw new Error('ERROR: useAamState must be used inside of AamProvider')
  }
  return context
}

function useAamDispatch() {
  const context = React.useContext(AamDispatchContext)
  if (context === undefined) {
    throw new Error('ERROR: useAamDispatch must be used inside of AamProvider')
  }
  return context
}

const nameTiles = [4, 27, 28]

const getAlpha2CountryCode = (code) => {
  if (!code) {
    return undefined
  }

  const trimmed = String(code).trim()
  if (!trimmed) {
    return undefined
  }

  const lower = trimmed.toLowerCase()
  const alpha2Match = countryDetails.countries.find((country) => country.alpha2 === lower)
  if (alpha2Match) {
    return alpha2Match.alpha2
  }
  // useuser hook returns three character country codes
  const alpha3Match = countryDetails.countries.find((country) => country.alpha3 === trimmed.toUpperCase())
  return alpha3Match?.alpha2
}
export const expandTileData = ({ tileData, person, year, atn, showRoman }) => {
  return tileData.map((item) => {
    let hasNameAsHeader = false
    if (item?.type.match(/globalNameCounts/i) || item?.type.match(/nameHeatmap/i)) {
      hasNameAsHeader = true
    }
    const isAsian = !!(checkAsianChar(person?.name) || checkAsianChar(person?.surname))
    const displayName = getDisplayName(person, showRoman)

    item.parsedNames = deconstructName(displayName, person?.name, person?.surname, isAsian, showRoman)
    item.personId = person?.personId
    item.gender = person.gender
    item.atn = atn
    item.needsDOB = !nameTiles.includes(item.id)
    if (item.header && item.header.match('{{NAME}}')) {
      item.header = item?.header?.replace('{{NAME}}', displayName)
    }
    if (item.header && item.header.match('{{COUNTRY}}')) {
      const matchingCountry = countryDetails.countries.find((country) => country.alpha2 === item.region)
      item.header = item.header.replace('{{COUNTRY}}', matchingCountry.name.replace(/\(.*?\)/, '').trim())
    }
    if (hasNameAsHeader && !item.header) {
      item.header = displayName
    }
    if (year) {
      item.year = year
    }
    return item
  })
}

function useAamInitialization(user) {
  // use this hook only once to load required application data on page load
  const dispatch = useAamDispatch()
  const dscState = useDSCState()
  // Gated on user.signedIn/personId - matching the enabled: Boolean(user?.signedIn
  // && user?.personId) guard useFamilyTreeData.js uses for the same endpoint.
  // Without this, an unauthenticated STATION session (guest nonstandardUser
  // cleared/missing) still fires this request, gets a 401, and zion-axios's
  // requireUserInterceptor treats that as an expired session and
  // force-reloads the page, causing an infinite reload loop.
  const [treeNameDataState] = useDetailedFamilyTreeData(user?.signedIn && user?.personId ? 5 : undefined)
  const [tileDataState, getTileData] = useTileData()
  const showRoman = usePreference('tree.showNamesInRomanScript')
  const ancestorTiles = useAncestorYearMatch(tileDataState, user, user.personId, treeNameDataState, showRoman)
  const showStatusOverlay = useStatusOverlay()
  const [hasRequested, setHasRequested] = useState(false)
  const resolvedCountryCode = React.useMemo(() => {
    const customCountry = getAlpha2CountryCode(dscState?.customCountry)
    if (customCountry) {
      return customCountry
    }

    const userCountry = getAlpha2CountryCode(user?.country)
    if (userCountry) {
      return userCountry
    }

    return 'us'
  }, [dscState.customCountry, user?.country])

  React.useEffect(() => {
    if (!user?.userLoading && user.signedIn && user.birthDate) {
      if (!hasRequested && !tileDataState.pending && !tileDataState.complete) {
        getTileData({
          countryCode: resolvedCountryCode,
          year: extractFourDigitYear(user.birthDate),
        })
        setHasRequested(true)
      }
    } else if (!user.userLoading) {
      // console("User isn't signed in")
    }
  }, [getTileData, hasRequested, resolvedCountryCode, tileDataState.complete, tileDataState.pending, user])

  React.useEffect(() => {
    if (
      tileDataState.complete &&
      tileDataState.res &&
      treeNameDataState.complete &&
      !user.userLoading &&
      user.personId
    ) {
      const selfTreePerson = treeNameDataState.res?.persons?.find((person) => person?.personId === user.personId)

      if (ancestorTiles && ancestorTiles.length !== 0) {
        ancestorTiles.forEach((ancestor) => tileDataState.res.push(ancestor))
      }

      const revisedTileData = expandTileData({
        tileData: tileDataState?.res,
        person: selfTreePerson,
        showRoman,
      })

      const treeData = treeNameDataState.res
      dispatch({
        type: ACTIONS.INIT_AAM,
        data: {
          tree: treeData.persons,
          tileData: revisedTileData,
          year: extractFourDigitYear(user.birthDate),
          countryCode: resolvedCountryCode,
          isGuestUser: false,
          showNamesInRomanScript: showRoman,
          person: selfTreePerson,
        },
      })
    } else if (tileDataState.complete && !tileDataState.res) {
      showStatusOverlay({
        message: 'No data found!',
        type: 'error',
      })
    }
  }, [
    ancestorTiles,
    dispatch,
    showStatusOverlay,
    tileDataState,
    tileDataState.complete,
    tileDataState.res,
    user.personId,
    user.birthDate,
    resolvedCountryCode,
    user.userLoading,
    showRoman,
    treeNameDataState.complete,
    treeNameDataState?.res,
  ])
}
// TO DO: Reduce duplication in these two hooks
function useAamGuestInitialization(nonstandardUser, appState) {
  const dispatch = useAamDispatch()
  const user = useEffectiveUser()
  const dscState = useDSCState()
  const [tileDataState, getTileData] = useTileData()
  const [hasRequested, setHasRequested] = useState(false)
  const dscDispatch = useDSCDispatch()
  const languageRegion = nonstandardUser?.languageRegion?.value?.split('_') ?? ['us', i18n.language]
  const showStatusOverlay = useStatusOverlay()

  setCookie('fslanguage', languageRegion[1])
  const region = languageRegion[0]

  React.useEffect(() => {
    if (
      !hasRequested &&
      !user.userLoading &&
      !appState.initialTileDataRequested &&
      nonstandardUser.initialized &&
      !nonstandardUser.authenticated &&
      nonstandardUser?.birthYear &&
      !tileDataState.pending &&
      !tileDataState.complete
    ) {
      getTileData({
        countryCode: region,
        year: Number.parseInt(nonstandardUser.birthYear, 10),
      })
      setHasRequested(true)
    } else if (shouldClearNonstandardUser(nonstandardUser)) {
      // We don't have a full set of data - blow it away so user will be prompted to enter it
      dscDispatch({
        type: DSC_ACTIONS.CLEAR_NONSTANDARD_USER,
      })
    }
  }, [
    appState.initialTileDataRequested,
    dispatch,
    dscDispatch,
    getTileData,
    hasRequested,
    nonstandardUser.authenticated,
    nonstandardUser.birthYear,
    nonstandardUser.initialized,
    region,
    tileDataState.complete,
    tileDataState.pending,
    user.userLoading,
  ])

  React.useEffect(() => {
    if (tileDataState.complete && tileDataState.res) {
      const { firstName, lastName, gender } = nonstandardUser
      const person = {
        name: firstName,
        surname: lastName,
        gender,
      }
      const revisedTileData = expandTileData({
        tileData: tileDataState.res,
        person,
      })

      dispatch({
        type: ACTIONS.INIT_AAM,
        data: {
          tileData: revisedTileData,
          year: extractFourDigitYear(nonstandardUser.birthYear),
          firstName,
          lastName,
          isGuestUser: true,
          guestUser: dscState?.nonstandardUser,
          person,
        },
      })
    } else if (tileDataState.complete && !tileDataState.res) {
      showStatusOverlay({
        message: 'No data found!',
        type: 'error',
      })
    }
  }, [
    dispatch,
    showStatusOverlay,
    tileDataState.complete,
    tileDataState.res,
    nonstandardUser,
    dscState.nonstandardUser,
  ])
}

export { AamProvider, useAamInitialization, useAamGuestInitialization, useAamState, useAamDispatch, ACTIONS }
